import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useWsNavigate } from "../../hooks/useWsNavigate";
import { usePageTitle } from "../../contexts/PageTitleContext";
import { useAgent } from "../../contexts/AgentContext";
import AgentPicker from "../../components/AgentPicker";
import PageHeader from "../../components/layout/PageHeader";
import Pagination from "../../components/ui/Pagination";
import Button from "../../components/ui/Button";
import CallUploadModal from "../../components/observability/CallUploadModal";
import { useAuth } from "../../contexts/AuthContext";
import { callObservationsAPI, observabilityAPI, alertRulesAPI, alertEventsAPI, metricsAPI, integrationAPI } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import {
  STANDARD_METRIC_FIELDS, BOOLEAN_VALUES, NO_VALUE_OPERATORS,
  getMetricResultType, getFieldOptions, getOperators,
} from "../../constants/metricConditions";
import CallPropertyBuilder from "../../components/filters/CallPropertyBuilder";
import useClickOutside from "../../hooks/useClickOutside";
import { useCan } from "../../hooks/useCan";
import {
  Plus,
  ExternalLink,
  ArrowRight,
  RefreshCw,
  ChevronDown,
  Columns2,
  GripVertical,
  SlidersHorizontal,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  Bell,
  Search,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Filter,
  Trash2,
} from "lucide-react";

const BASE_COLUMNS = [
  { key: "callIdentifier", label: "Call Identifier" },
  { key: "agent", label: "Assistant" },
  { key: "callSuccess", label: "Call Success" },
  { key: "endedReason", label: "Call End Reason" },
  { key: "phoneNumber", label: "Phone Number" },
  { key: "avgLatency", label: "Avg Latency" },
  { key: "userInt", label: "User Int." },
  { key: "aiInt", label: "AI Int." },
  { key: "duration", label: "Duration" },
  { key: "callTimestamp", label: "Timestamp" },
];

// Default column widths (px) used by the resizable table; falls back to FALLBACK_COL_WIDTH.
const DEFAULT_COL_WIDTHS = {
  callIdentifier: 200,
  agent: 150,
  callSuccess: 110,
  endedReason: 150,
  phoneNumber: 140,
  avgLatency: 110,
  userInt: 90,
  aiInt: 90,
  duration: 100,
  callTimestamp: 160,
};
const FALLBACK_COL_WIDTH = 160;
const CHECKBOX_COL_WIDTH = 44;
const MIN_COL_WIDTH = 80;

// Page-size sizing: fallback heights used when the real rows haven't rendered yet.
// The actual page size is measured from the table area's CSS-computed height.
const ROW_H = 49;          // approx height of one body row (px-4 py-3 + text-sm)
const HEADER_ROW_H = 45;   // approx height of the table header row
const BOTTOM_PAD = 8;      // breathing room at the bottom of the table area
const MIN_ROWS = 5;

const Conversations = () => {
  const { setPageTitle } = usePageTitle();
  const { agents: contextAgents, selectedAgent, setSelectedAgent } = useAgent();
  const canManualUpload = useCan("manual_upload", "write");
  const canReanalyze = useCan("reanalyze", "write");
  useEffect(() => {
    setPageTitle("Conversations");
    return () => setPageTitle(null);
  }, [setPageTitle]);

  const navigate = useWsNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentWorkspace, workspaces, switchWorkspace } = useAuth();
  const { showToast } = useToast();
  const [fetchingCalls, setFetchingCalls] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Server page size, sized so the table fills its area without scrolling.
  const [perPage, setPerPage] = useState(15);
  // The flex region that holds the table. Its height is computed by CSS (the space
  // left after the toolbar + pagination), so measuring it is deterministic and does
  // not depend on render timing — unlike measuring viewport-relative positions.
  const tableAreaRef = useRef(null);

  // How many rows fit in the table area: (area height - table header) / row height.
  // Uses real measured header/row heights, falling back to estimates, and floor() so
  // it can never overshoot into a scrollbar.
  const measureRows = () => {
    const area = tableAreaRef.current;
    if (!area) return perPage;
    const areaH = area.clientHeight;
    if (!areaH) return perPage;
    const headEl = area.querySelector("thead tr");
    const rowEl = area.querySelector("tbody tr");
    const headH = headEl?.getBoundingClientRect().height || HEADER_ROW_H;
    const rowH = rowEl?.getBoundingClientRect().height || ROW_H;
    return Math.max(MIN_ROWS, Math.floor((areaH - headH - BOTTOM_PAD) / rowH));
  };
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const agents = contextAgents; // use agents from context — no separate fetch
  const [selectedIds, setSelectedIds] = useState([]);
  const [rerunDropdownOpen, setRerunDropdownOpen] = useState(false);
  const [rerunMode, setRerunMode] = useState("menu"); // "menu" | "select"
  const [rerunMetricIds, setRerunMetricIds] = useState([]);
  const [filters, setFilters] = useState({ search: "", direction: "", endReason: "", analysisStatus: "", tag: "", dateFrom: "", dateTo: "", metricCondition: null, callPropertyCondition: null });
  const [alertFilter, setAlertFilter] = useState(null); // { rule_id, rule_name, conditions }
  const [allMetrics, setAllMetrics] = useState([]);
  // Metric condition builder state (controlled React, not DOM)
  const [mcMetricId, setMcMetricId] = useState("");
  const [mcField, setMcField] = useState("");
  const [mcOperator, setMcOperator] = useState("equals");
  const [mcValue, setMcValue] = useState("");
  const [visibleColumns, setVisibleColumns] = useState(
    () => new Set(BASE_COLUMNS.map((c) => c.key)),
  );
  const [columnOrder, setColumnOrder] = useState(() =>
    BASE_COLUMNS.map((c) => c.key),
  );
  // User-overridden column widths (px), keyed by column key. Falls back to defaults.
  const [columnWidths, setColumnWidths] = useState({});
  // Build metric columns from allMetrics (custom only — audio metrics already have dedicated columns)
  const metricColumns = React.useMemo(() =>
    allMetrics
      .map((m) => ({ key: `metric_${m.id}`, label: m.name, metricId: m.id, resultType: m.result_type, isCritical: m.is_critical })),
    [allMetrics]
  );
  const allColumnDefs = React.useMemo(() => [...BASE_COLUMNS, ...metricColumns], [metricColumns]);

  // When metric columns load, add new keys to columnOrder and make them visible
  React.useEffect(() => {
    if (metricColumns.length === 0) return;
    const newKeys = metricColumns.map((c) => c.key);
    setColumnOrder((prev) => {
      const existing = new Set(prev);
      const toAdd = newKeys.filter((k) => !existing.has(k));
      return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
    });
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      newKeys.forEach((k) => next.add(k));
      return next;
    });
  }, [metricColumns]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null }); // { key: colKey, direction: "asc"|"desc" }
  const [columnFilters, setColumnFilters] = useState({}); // { colKey: filterValue }
  const [activeFilterCol, setActiveFilterCol] = useState(null); // which column filter dropdown is open
  const filterDropdownRef = useRef(null);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [draggedCol, setDraggedCol] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const columnsMenuRef = useRef(null);
  const filterMenuRef = useRef(null);

  // Only providers we actually support for call ingestion are shown:
  //   • Custom API — push calls via REST (jumps to the code sample below)
  //   • Vapi / Retell — connect an account to auto-sync (Settings → Integrations)
  const apiSectionRef = useRef(null);
  const providers = [
    {
      id: "custom-api",
      name: "Custom API",
      img: "/icons/custom.svg",
      desc: "Push calls straight from your own stack via REST.",
      cta: "See the code",
      tag: "Popular",
      action: "scroll",
    },
    {
      id: "vapi",
      name: "Vapi",
      img: "/icons/vapi-icon.svg",
      desc: "Auto-sync every call from your Vapi account.",
      cta: "Connect",
      action: "integrations",
    },
    {
      id: "retell",
      name: "Retell",
      img: "/icons/retell-icon.svg",
      desc: "Auto-sync every call from your Retell account.",
      cta: "Connect",
      action: "integrations",
    },
  ];

  const handleProviderClick = (provider) => {
    if (provider.action === "scroll") {
      apiSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (provider.action === "integrations") {
      navigate("/settings/integrations");
    }
  };

  const [selectedLanguage, setSelectedLanguage] = useState("curl");
  const codeExamples = {
    python: `import requests

url = 'https://api.rubrichq.io/api/public/v1/calls'
headers = {
    'Authorization': 'Bearer <your_api_key>',
    'Content-Type': 'application/json'
}
data = {
    'call_identifier': '<unique_call_id>',
    'agent_id': '<agent_id>',
    'client_id': '<workspace_id>',
    'recording_url': '<url_to_call_recording>',
    'call_end_reason': 'completed'
}
response = requests.post(url, headers=headers, json=data)`,
    javascript: `const axios = require('axios');

const url = 'https://api.rubrichq.io/api/public/v1/calls';
const headers = {
  'Authorization': 'Bearer <your_api_key>',
  'Content-Type': 'application/json'
};
const data = {
  call_identifier: '<unique_call_id>',
  agent_id: '<agent_id>',
  client_id: '<workspace_id>',
  recording_url: '<url_to_call_recording>',
  call_end_reason: 'completed'
};
axios.post(url, data, { headers });`,
    curl: `curl -X POST 'https://api.rubrichq.io/api/public/v1/calls' \\
  -H 'Authorization: Bearer <your_api_key>' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "call_identifier": "<unique_call_id>",
    "agent_id": "<agent_id>",
    "client_id": "<workspace_id>",
    "recording_url": "<url_to_call_recording>",
    "call_end_reason": "completed"
  }'`,
  };

  // Load alert context from URL params.
  //   ?alert_event_id=N → the EXACT calls this notification matched when it fired
  //   ?alert_rule_id=N  → live view of the rule (its conditions, last X min from now)
  const alertRuleId = searchParams.get("alert_rule_id");
  const alertEventId = searchParams.get("alert_event_id");
  const targetWorkspaceId = searchParams.get("workspace_id");

  // A notification CTA carries the workspace that owns the alert. If the recipient is
  // viewing a different workspace, switch to the owning one first — otherwise the event
  // (scoped by client_id) won't resolve and no calls would show.
  useEffect(() => {
    if (!targetWorkspaceId || !currentWorkspace || !workspaces?.length) return;
    if (String(currentWorkspace.id) === String(targetWorkspaceId)) return;
    const target = workspaces.find((w) => String(w.id) === String(targetWorkspaceId));
    if (target) switchWorkspace(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetWorkspaceId, currentWorkspace, workspaces]);

  useEffect(() => {
    if (alertEventId && currentWorkspace) {
      alertEventsAPI.get(currentWorkspace.id, alertEventId).then((ev) => {
        if (ev) {
          const mc = ev.matched_conditions || {};
          setAlertFilter({
            event_id: ev.id,
            rule_id: ev.alert_rule_id,
            rule_name: ev.alert_rule_name,
            fired_at: ev.created_at,
            match_count: mc.match_count,
          });
        }
      }).catch(() => {});
    } else if (alertRuleId && currentWorkspace) {
      alertRulesAPI.list(currentWorkspace.id).then((rules) => {
        const rule = (rules || []).find((r) => r.id === parseInt(alertRuleId));
        if (rule) {
          setAlertFilter({ rule_id: rule.id, rule_name: rule.name, conditions: rule.conditions, time_window_minutes: rule.time_window_minutes });
        }
      }).catch(() => {});
    } else if (!alertRuleId && !alertEventId && alertFilter) {
      setAlertFilter(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertRuleId, alertEventId, currentWorkspace]);

  useEffect(() => {
    if (currentWorkspace) fetchMetricsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace]);

  useEffect(() => {
    if (!currentWorkspace) return;
    // If the URL requests an alert filter that hasn't resolved yet, wait for it —
    // otherwise we'd fetch once unfiltered and again once alertFilter loads (a visible
    // double-load / "refreshing multiple times").
    if ((alertEventId || alertRuleId) && !alertFilter) return;
    fetchCallObservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace, currentPage, perPage, filters.direction, filters.endReason, filters.analysisStatus, filters.tag, filters.dateFrom, filters.dateTo, filters.metricCondition, selectedAgent, alertFilter]);

  // Size the page so the table fills its flex area without scrolling. The area's
  // height is determined by CSS (viewport minus toolbar/pagination), so a
  // ResizeObserver on it gives a stable, identical result on every refresh — no
  // dependence on render timing or scroll position. Re-attaches when the table
  // re-mounts after a load (keyed on `loading`).
  useEffect(() => {
    const area = tableAreaRef.current;
    if (!area) return;
    const apply = () => {
      setPerPage((prev) => {
        const next = measureRows();
        return prev === next ? prev : next;
      });
    };
    const ro = new ResizeObserver(apply);
    ro.observe(area);
    apply();
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // When the page size changes, return to page 1 so currentPage can't exceed the
  // new total page count.
  useEffect(() => {
    setCurrentPage(1);
  }, [perPage]);

  useClickOutside(columnsMenuRef, () => setShowColumnsMenu(false), showColumnsMenu);
  useClickOutside(filterMenuRef, () => setShowFilterMenu(false), showFilterMenu);

  const fetchMetricsData = async () => {
    try {
      const mRes = await metricsAPI.getAllMetrics(currentWorkspace.id, { per_page: 999 });
      const standard = mRes?.standard_metrics || [];
      const custom = mRes?.data || [];
      const global = mRes?.global_metrics || [];
      setAllMetrics([...standard, ...custom, ...global]);
    } catch {}
  };

  const fetchCallObservations = async () => {
    if (!currentWorkspace) return;
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: perPage,
        // When an alert filter is active, the alert's own scope (its rule's agent +
        // the matched window/conditions) is authoritative. Don't layer the page's
        // currently-selected agent on top — that would wrongly hide matched calls
        // belonging to a different agent than the one selected in the UI.
        agent_id: alertFilter ? undefined : (selectedAgent?.id || undefined),
        search: filters.search || undefined,
        call_direction: filters.direction || undefined,
        call_end_reason: filters.endReason || undefined,
        analysis_status: filters.analysisStatus || undefined,
        tag: filters.tag || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        alert_event_id: alertFilter?.event_id || undefined,
        alert_rule_id: (!alertFilter?.event_id && alertFilter?.rule_id) || undefined,
      };
      const mc = [];
      if (filters.metricCondition) mc.push(filters.metricCondition);
      if (filters.callPropertyCondition) mc.push(filters.callPropertyCondition);
      if (mc.length > 0) params.metric_conditions = JSON.stringify(mc);
      const response = await callObservationsAPI.getCallObservations(
        currentWorkspace.id,
        params,
      );
      if (response.data && response.data.call_observations) {
        const transformed = response.data.call_observations.map((call) => ({
          id: call.id,
          callIdentifier: call.call_identifier || `#${call.id}`,
          agent: call.agent,
          endedReason: call.call_end_reason || "—",
          phoneNumber: call.phone_number || call.caller_phone_number || "—",
          callTimestamp: call.call_started_at || call.created_at,
          transcriptionStatus: call.transcription_status,
          analysisStatus: call.analysis_status,
          metricSummary: call.metric_summary || {},
          ...call,
        }));
        setCalls(transformed);
        if (response.data.meta)
          setTotalPages(response.data.meta.total_pages || 1);
      }
    } catch (err) {
      console.error("Error fetching call observations:", err);
      setCalls([]);
    } finally {
      setLoading(false);
    }
  };

  // Vapi/Retell agents can pull recent calls from the provider on demand. Only
  // these external voice platforms support fetching; other integration types
  // (pipecat, websocket, etc.) push calls to us instead.
  const canFetchCalls =
    !alertFilter &&
    selectedAgent &&
    ["external_vapi", "external_retell"].includes(selectedAgent.integration_type);

  const handleFetchCalls = async () => {
    if (!canFetchCalls || fetchingCalls) return;
    setFetchingCalls(true);
    try {
      const res = await integrationAPI.fetchCalls(
        currentWorkspace.id,
        selectedAgent.id,
      );
      const n = res.imported || 0;
      showToast(
        n > 0
          ? `Imported ${n} new call${n !== 1 ? "s" : ""}`
          : "No new calls to import",
        "success",
      );
      if (n > 0) fetchCallObservations();
    } catch (err) {
      showToast(
        err?.response?.data?.error || "Failed to fetch calls",
        "error",
      );
    } finally {
      setFetchingCalls(false);
    }
  };

  const handleUploadSuccess = () => {
    fetchCallObservations();
    setIsUploadModalOpen(false);
  };

  const toggleSelectAll = () => {
    setSelectedIds(
      selectedIds.length === calls.length ? [] : calls.map((c) => c.id),
    );
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const closeRerunDropdown = () => {
    setRerunDropdownOpen(false);
    setRerunMode("menu");
  };

  const toggleRerunMetric = (metricId) => {
    setRerunMetricIds((prev) =>
      prev.includes(metricId) ? prev.filter((x) => x !== metricId) : [...prev, metricId]
    );
  };

  const markCallsPending = (ids) =>
    setCalls((prev) =>
      prev.map((c) => (ids.includes(c.id) ? { ...c, analysis_status: "pending" } : c))
    );

  const handleRerun = async (type) => {
    const ids = selectedIds.length > 0 ? selectedIds : calls.map((c) => c.id);
    closeRerunDropdown();

    try {
      const rerunFn =
        type === "all" ? observabilityAPI.rerunAllMetrics :
        type === "standard" ? observabilityAPI.rerunAudioMetrics :
        observabilityAPI.rerunLlmMetrics;

      await Promise.all(ids.map((id) => rerunFn(id)));
      markCallsPending(ids);
    } catch (err) {
      console.error("Rerun failed:", err);
    }
  };

  const handleRerunSelectedMetrics = async () => {
    if (!rerunMetricIds.length) return;
    const ids = selectedIds.length > 0 ? selectedIds : calls.map((c) => c.id);
    closeRerunDropdown();

    try {
      await Promise.all(ids.map((id) => observabilityAPI.rerunSelectedMetrics(id, rerunMetricIds)));
      markCallsPending(ids);
      setRerunMetricIds([]);
    } catch (err) {
      console.error("Selected-metric rerun failed:", err);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0 || deleting) return;
    const n = selectedIds.length;
    const ok = window.confirm(
      `Delete ${n} conversation${n !== 1 ? "s" : ""}? This permanently removes the transcript, recording, and all metric results. This cannot be undone.`,
    );
    if (!ok) return;

    setDeleting(true);
    try {
      await Promise.all(
        selectedIds.map((id) =>
          callObservationsAPI.deleteCallObservation(currentWorkspace.id, id),
        ),
      );
      const clearedWholePage = n >= calls.length;
      setSelectedIds([]);
      showToast(
        `Deleted ${n} conversation${n !== 1 ? "s" : ""}`,
        "success",
      );
      // Reload from the server so rows from the next page fill the gap left by
      // the deletions. If we just emptied a non-first page, step back one page
      // (which retriggers the fetch effect) so we don't land on a blank page.
      if (clearedWholePage && currentPage > 1) {
        setCurrentPage((p) => p - 1);
      } else {
        fetchCallObservations();
      }
    } catch (err) {
      console.error("Delete failed:", err);
      showToast(
        err?.response?.data?.error || "Failed to delete conversations",
        "error",
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleColumnDrop = (targetKey) => {
    if (!draggedCol || draggedCol === targetKey) return;
    const newOrder = [...columnOrder];
    const fromIdx = newOrder.indexOf(draggedCol);
    const toIdx = newOrder.indexOf(targetKey);
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, draggedCol);
    setColumnOrder(newOrder);
    setDraggedCol(null);
    setDragOverCol(null);
  };

  const orderedVisibleColumns = columnOrder
    .filter((key) => visibleColumns.has(key))
    .map((key) => allColumnDefs.find((c) => c.key === key))
    .filter(Boolean);

  // --- Column resizing ---
  const colWidthFor = (key) =>
    columnWidths[key] ?? DEFAULT_COL_WIDTHS[key] ?? FALLBACK_COL_WIDTH;

  const startResize = (e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = colWidthFor(colKey);
    const onMove = (ev) => {
      const next = Math.max(MIN_COL_WIDTH, startWidth + (ev.clientX - startX));
      setColumnWidths((p) => ({ ...p, [colKey]: next }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const tableWidth =
    CHECKBOX_COL_WIDTH +
    orderedVisibleColumns.reduce((sum, c) => sum + colWidthFor(c.key), 0);

  useClickOutside(filterDropdownRef, () => setActiveFilterCol(null), !!activeFilterCol);

  // --- Sorting ---
  const handleSort = (colKey) => {
    setSortConfig((prev) => {
      if (prev.key === colKey) {
        if (prev.direction === "asc") return { key: colKey, direction: "desc" };
        if (prev.direction === "desc") return { key: null, direction: null };
      }
      return { key: colKey, direction: "asc" };
    });
  };

  const getCellValue = (call, colKey) => {
    switch (colKey) {
      case "callIdentifier": return call.callIdentifier;
      case "agent": return call.agent?.name || "";
      case "callSuccess": return call.call_success === true ? 1 : 0;
      case "endedReason": return call.endedReason;
      case "phoneNumber": return call.phoneNumber;
      case "avgLatency": return call.metricSummary?.avg_latency_ms ?? -1;
      case "userInt": return call.metricSummary?.user_interruptions ?? -1;
      case "aiInt": return call.metricSummary?.ai_interruptions ?? -1;
      case "duration": return call.call_duration ?? -1;
      case "callTimestamp": return call.callTimestamp ? new Date(call.callTimestamp).getTime() : 0;
      default:
        if (colKey.startsWith("metric_")) {
          const mid = colKey.replace("metric_", "");
          const mv = call.metric_values?.[mid];
          if (!mv || mv.status !== "computed") return "";
          const v = String(mv.value ?? "").toLowerCase();
          if (v === "pass" || v === "passed" || v === "true") return 1;
          if (v === "fail" || v === "failed" || v === "false") return 0;
          return mv.value ?? "";
        }
        return "";
    }
  };

  // --- Column filters ---
  // Returns { type: "select", options: [...] } or { type: "search" } or null
  const getFilterConfig = (colKey) => {
    if (colKey === "callSuccess") return { type: "select", options: [{ value: "pass", label: "Pass" }, { value: "fail", label: "Fail" }] };
    if (colKey === "endedReason") {
      const unique = [...new Set(calls.map((c) => c.endedReason).filter((v) => v && v !== "—"))];
      // A filter with fewer than 2 distinct values can't narrow anything — don't show it.
      return unique.length > 1 ? { type: "select", options: unique.map((v) => ({ value: v, label: v.replace(/-/g, " ") })) } : null;
    }
    if (colKey.startsWith("metric_")) {
      const col = allColumnDefs.find((c) => c.key === colKey);
      const rt = col?.resultType;
      if (rt === "boolean") return { type: "select", options: [{ value: "pass", label: "Pass" }, { value: "fail", label: "Fail" }] };
      if (rt === "rating") return { type: "select", options: [1, 2, 3, 4, 5].map((v) => ({ value: String(v), label: `${v} / 5` })) };
      if (rt === "enum") {
        const mid = colKey.replace("metric_", "");
        const unique = [...new Set(calls.map((c) => c.metric_values?.[mid]?.value).filter((v) => v != null && v !== ""))];
        // Only offer a select when there are at least 2 distinct values to choose between; otherwise fall back to search.
        return unique.length > 1 ? { type: "select", options: unique.map((v) => ({ value: String(v), label: String(v) })) } : { type: "search" };
      }
      // numeric, or unknown/system metrics — text search
      return { type: "search" };
    }
    return null;
  };

  const matchesFilter = (call, colKey, filterVal) => {
    if (!filterVal) return true;
    if (colKey === "callSuccess") {
      return filterVal === "pass" ? call.call_success === true : call.call_success !== true;
    }
    if (colKey === "endedReason") return call.endedReason === filterVal;
    if (colKey.startsWith("metric_")) {
      const mid = colKey.replace("metric_", "");
      const mv = call.metric_values?.[mid];
      const v = String(mv?.value ?? "").toLowerCase();
      const fv = filterVal.toLowerCase();
      // Select-based filters (exact match)
      if (fv === "pass") return v === "pass" || v === "passed" || v === "true";
      if (fv === "fail") return v === "fail" || v === "failed" || v === "false" || !mv || mv.status !== "computed";
      // Text search (contains)
      return v.includes(fv);
    }
    return true;
  };

  // Apply column filters then sort
  const displayCalls = React.useMemo(() => {
    let result = [...calls];
    // Apply column filters
    Object.entries(columnFilters).forEach(([colKey, filterVal]) => {
      if (filterVal) result = result.filter((c) => matchesFilter(c, colKey, filterVal));
    });
    // Apply sort
    if (sortConfig.key) {
      result.sort((a, b) => {
        const va = getCellValue(a, sortConfig.key);
        const vb = getCellValue(b, sortConfig.key);
        if (va < vb) return sortConfig.direction === "asc" ? -1 : 1;
        if (va > vb) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [calls, columnFilters, sortConfig, allColumnDefs]);

  const noData = <span className="text-sm text-muted-foreground/60 italic">No data</span>;
  const textNA = <span className="text-sm text-muted-foreground/60" title="Not applicable for text conversations">—</span>;

  const renderAnalysisIcon = (status) => {
    switch (status) {
      case "computed":
      case "completed":
        return <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" title="Analysis complete" />;
      case "processing":
        return <Loader2 className="h-3.5 w-3.5 text-amber-500 animate-spin flex-shrink-0" title="Analysis in progress" />;
      case "pending":
        return <Clock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" title="Analysis pending" />;
      case "failed":
        return <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" title="Analysis failed" />;
      default:
        return null;
    }
  };

  const END_REASON_STYLES = {
    completed: "bg-green-50 text-green-700",
    "customer-ended": "bg-blue-50 text-blue-700",
    "assistant-ended": "bg-indigo-50 text-indigo-700",
    "silence-timed-out": "bg-orange-50 text-orange-700",
    voicemail: "bg-purple-50 text-purple-700",
  };

  const renderEndReason = (reason) => {
    if (!reason || reason === "—") return noData;
    const cls = END_REASON_STYLES[reason] || "bg-muted text-muted-foreground";
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${cls}`}
      >
        {reason.replace(/-/g, " ")}
      </span>
    );
  };

  const renderCell = (call, col) => {
    const onClick = () => navigate(`/observability/conversations/${call.id}`);
    const base = "px-4 py-3 cursor-pointer overflow-hidden";
    switch (col.key) {
      case "callIdentifier":
        return (
          <td key={col.key} className={base} onClick={onClick}>
            <div className="flex items-center gap-1.5 min-w-[160px]">
              {renderAnalysisIcon(call.analysisStatus)}
              <ExternalLink className="h-3.5 w-3.5 text-indigo-400 flex-shrink-0" />
              <span className="text-sm font-medium text-indigo-600 hover:text-indigo-800 truncate">
                {call.callIdentifier}
              </span>
            </div>
          </td>
        );
      case "agent":
        return (
          <td key={col.key} className={base} onClick={onClick}>
            <span className="text-sm text-foreground truncate block min-w-[120px]">
              {call.agent?.name || noData}
            </span>
          </td>
        );
      case "callSuccess":
        return (
          <td key={col.key} className={base} onClick={onClick}>
            {call.call_success === true ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> Pass
              </span>
            ) : call.call_success === false ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
                <XCircle className="h-3 w-3" /> Fail
              </span>
            ) : call.analysis_status === "error" || call.analysis_status === "failed" ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">
                Error
              </span>
            ) : call.analysis_status === "completed" ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                Evaluating
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                Pending
              </span>
            )}
          </td>
        );
      case "endedReason":
        return (
          <td
            key={col.key}
            className={`${base} min-w-[140px]`}
            onClick={onClick}
          >
            {renderEndReason(call.endedReason)}
          </td>
        );
      case "phoneNumber":
        return (
          <td key={col.key} className={base} onClick={onClick}>
            <span className="text-sm text-muted-foreground truncate block min-w-[130px]">
              {!call.phoneNumber || call.phoneNumber === "—"
                ? noData
                : call.phoneNumber}
            </span>
          </td>
        );
      case "avgLatency":
        return (
          <td key={col.key} className={base} onClick={onClick}>
            <span className="text-sm text-muted-foreground truncate block min-w-[90px]">
              {call.observation_type === "text"
                ? textNA
                : call.metricSummary?.avg_latency_ms != null
                ? `${Math.round(call.metricSummary.avg_latency_ms)} ms`
                : noData}
            </span>
          </td>
        );
      case "userInt":
        return (
          <td key={col.key} className={base} onClick={onClick}>
            <span className="text-sm text-muted-foreground min-w-[70px] block">
              {call.observation_type === "text"
                ? textNA
                : call.metricSummary?.user_interruptions != null
                ? call.metricSummary.user_interruptions
                : noData}
            </span>
          </td>
        );
      case "aiInt":
        return (
          <td key={col.key} className={base} onClick={onClick}>
            <span className="text-sm text-muted-foreground min-w-[70px] block">
              {call.observation_type === "text"
                ? textNA
                : call.metricSummary?.ai_interruptions != null
                ? call.metricSummary.ai_interruptions
                : noData}
            </span>
          </td>
        );
      case "duration":
        return (
          <td key={col.key} className={base} onClick={onClick}>
            <span className="text-sm text-muted-foreground truncate block">
              {call.call_duration
                ? formatDuration(call.call_duration)
                : noData}
            </span>
          </td>
        );
      case "callTimestamp":
        return (
          <td key={col.key} className={base} onClick={onClick}>
            <span className="text-sm text-muted-foreground truncate block min-w-[150px]">
              {call.callTimestamp
                ? formatTimestamp(call.callTimestamp)
                : noData}
            </span>
          </td>
        );
      default: {
        // Dynamic metric columns (key = "metric_{id}")
        if (col.key.startsWith("metric_")) {
          const mv = call.metric_values?.[String(col.metricId)];
          const val = mv?.value;
          const status = mv?.status;
          const rt = col.resultType || "boolean";

          let display;
          if (!mv || status === "pending") {
            display = <span className="text-xs text-muted-foreground">Pending</span>;
          } else if (status === "failed") {
            display = <span className="text-xs text-red-400">Error</span>;
          } else if (rt === "boolean") {
            const passed = String(val).toLowerCase() === "pass" || String(val).toLowerCase() === "passed" || val === true;
            display = (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${passed ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                {passed ? "Pass" : "Fail"}
              </span>
            );
          } else if (val != null) {
            display = <span className="text-sm text-foreground">{String(val)}</span>;
          } else {
            display = noData;
          }

          return (
            <td key={col.key} className={base} onClick={onClick}>
              {display}
            </td>
          );
        }
        return <td key={col.key} className={base} onClick={onClick}>{noData}</td>;
      }
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "—";
    const d = new Date(ts);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  // Formats a call duration (in seconds) as "Xm Ys" or "Ys"
  const formatDuration = (seconds) => {
    const total = Math.round(Number(seconds));
    if (!Number.isFinite(total) || total <= 0) return "—";
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  // Only show onboarding when there are truly no calls (no filters active)
  const hasActiveFilters = selectedAgent || filters.direction || filters.endReason || filters.analysisStatus || filters.tag || filters.dateFrom || filters.metricCondition || filters.callPropertyCondition || alertFilter || Object.keys(columnFilters).length > 0;
  const showIntegrationMethods = !loading && calls.length === 0 && !hasActiveFilters;

  return (
    <div className="px-4 flex flex-col h-full min-h-0">
      <PageHeader description="View and analyze all your conversation calls." />

      {/* Alert filter banner */}
      {alertFilter && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-amber-600" />
            <span className="text-sm text-amber-800">
              Filtered by alert: <span className="font-semibold">{alertFilter.rule_name}</span>
            </span>
            {alertFilter.event_id ? (
              <>
                {alertFilter.match_count != null && (
                  <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                    {alertFilter.match_count} matched
                  </span>
                )}
                {alertFilter.fired_at && (
                  <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                    fired {new Date(alertFilter.fired_at).toLocaleString()}
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                  {alertFilter.conditions?.length || 0} condition{alertFilter.conditions?.length !== 1 ? "s" : ""}
                </span>
                {alertFilter.time_window_minutes && (
                  <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                    last {alertFilter.time_window_minutes} min
                  </span>
                )}
              </>
            )}
          </div>
          <button
            onClick={() => {
              setAlertFilter(null);
              setSearchParams({});
              setCurrentPage(1);
            }}
            className="text-sm text-amber-700 hover:text-amber-900 font-medium flex items-center gap-1"
          >
            Clear filter
            <span className="text-lg leading-none">&times;</span>
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mb-4"></div>
            <p className="text-sm text-muted-foreground">Loading conversations...</p>
          </div>
        </div>
      ) : showIntegrationMethods && !selectedAgent ? (
        <div className="max-w-6xl mx-auto py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              No conversation data yet
            </h2>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto">
              Connect your voice provider or push calls to start tracking and
              analyzing your conversations.
            </p>
          </div>

          {/* Two paths: connect (left) · API (right) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 rounded-2xl border border-border overflow-hidden bg-card shadow-sm">
            {/* LEFT — add a call manually + connect a provider */}
            <div className="p-6 lg:p-8 lg:border-r border-border">
              {canManualUpload && (
                <>
                  <Button
                    variant="black"
                    onClick={() => setIsUploadModalOpen(true)}
                    className="w-full justify-center px-6 py-3 text-sm"
                  >
                    <Plus className="h-5 w-5" strokeWidth={1.5} />
                    <span>Add Call Manually</span>
                  </Button>
                  <button
                    onClick={() =>
                      window.open("https://docs.rubrichq.io", "_blank")
                    }
                    className="mt-3 mx-auto flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span>View documentation</span>
                    <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>

                  <div className="flex items-center gap-3 my-6">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      or connect a provider
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                </>
              )}

              {!canManualUpload && (
                <h3 className="text-sm font-semibold text-foreground mb-4">
                  Connect your voice provider
                </h3>
              )}

              <div className="flex flex-col gap-3">
                {providers.map((provider) => (
                  <button
                    key={provider.id}
                    onClick={() => handleProviderClick(provider)}
                    className={`group flex items-center gap-4 w-full text-left bg-card border rounded-xl px-4 py-3.5 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${
                      provider.tag
                        ? "border-primary/40 ring-1 ring-primary/10"
                        : "border-border hover:border-primary/40"
                    }`}
                  >
                    <div className="h-11 w-11 shrink-0 rounded-lg border border-border bg-muted/40 flex items-center justify-center overflow-hidden">
                      <img
                        src={provider.img}
                        alt={`${provider.name} logo`}
                        className="h-7 w-7 rounded object-contain transition-transform duration-200 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground">
                          {provider.name}
                        </p>
                        {provider.tag && (
                          <span className="bg-primary text-primary-foreground text-[10px] font-semibold px-2 py-0.5 rounded-full">
                            {provider.tag}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {provider.desc}
                      </p>
                    </div>
                    <ArrowRight
                      className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground transition-all duration-200 group-hover:translate-x-0.5"
                      strokeWidth={1.5}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* RIGHT — push calls via API */}
            <div
              ref={apiSectionRef}
              className="scroll-mt-4 p-6 lg:p-8 bg-muted/30 flex flex-col"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-1">
                    Or connect with API
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Push calls directly with our REST API.
                  </p>
                </div>
                <a
                  href="https://docs.rubrichq.io/observability"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-card border border-border rounded-lg hover:border-primary/40 transition-all text-xs font-medium text-foreground shadow-sm"
                >
                  <span>Full API Docs</span>
                  <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                </a>
              </div>
              <div className="flex-1 bg-card rounded-xl overflow-hidden shadow-lg border border-border flex flex-col min-h-0">
                <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-gray-800 to-gray-900 border-b border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <select
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      className="px-2.5 py-1 bg-gray-700 text-gray-100 text-xs rounded-md border border-gray-600 focus:outline-none cursor-pointer"
                    >
                      <option value="curl">cURL</option>
                      <option value="python">Python</option>
                      <option value="javascript">JavaScript</option>
                    </select>
                  </div>
                  <button
                    onClick={() =>
                      navigator.clipboard.writeText(
                        codeExamples[selectedLanguage],
                      )
                    }
                    className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-100 text-xs rounded-md transition-colors"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    <span>Copy</span>
                  </button>
                </div>
                <div className="flex-1 p-4 bg-gray-900 overflow-auto">
                  <pre className="text-xs text-gray-100 font-mono leading-relaxed">
                    <code>{codeExamples[selectedLanguage]}</code>
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap shrink-0">
            {/* Left: sophisticated filter popover */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative" ref={filterMenuRef}>
                {(() => {
                  const activeCount = [
                    filters.direction,
                    filters.endReason,
                    filters.analysisStatus,
                    filters.tag,
                    filters.dateFrom || filters.dateTo,
                    filters.metricCondition,
                    filters.callPropertyCondition,
                  ].filter(Boolean).length;
                  return (
                    <>
                      <button
                        onClick={() => setShowFilterMenu((v) => !v)}
                        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg bg-card transition-colors ${
                          activeCount > 0
                            ? "border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                            : "border-border text-foreground hover:bg-muted/50"
                        }`}
                      >
                        <SlidersHorizontal className="h-4 w-4" />
                        <span>Filters</span>
                        {activeCount > 0 && (
                          <span className="flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-indigo-600 text-white text-xs font-bold leading-none">
                            {activeCount}
                          </span>
                        )}
                        <ChevronDown
                          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${showFilterMenu ? "rotate-180" : ""}`}
                        />
                      </button>

                      {showFilterMenu && (() => {
                        const selectedMetric = allMetrics.find((m) => m.id === parseInt(mcMetricId));
                        const fieldOptions = getFieldOptions(selectedMetric);
                        const operators = getOperators(selectedMetric);
                        const resultType = getMetricResultType(selectedMetric);
                        const hideValue = NO_VALUE_OPERATORS.includes(mcOperator);

                        return (
                        <div className="absolute left-0 mt-1 w-80 bg-popover border border-border rounded-xl shadow-xl z-30 p-4 max-h-[520px] overflow-y-auto">
                          {/* Date Range */}
                          <div className="mb-4">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Date Range</p>
                            <div className="flex gap-2">
                              <input type="date" value={filters.dateFrom} onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                                className="flex-1 text-xs border border-input rounded-lg px-2 py-1.5 bg-background" />
                              <input type="date" value={filters.dateTo} onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                                className="flex-1 text-xs border border-input rounded-lg px-2 py-1.5 bg-background" />
                            </div>
                          </div>

                          {/* Direction */}
                          <div className="mb-4">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Direction</p>
                            <div className="flex items-center bg-muted rounded-lg p-0.5 gap-0.5">
                              {[{ value: "", label: "All" }, { value: "inbound", label: "Inbound" }, { value: "outbound", label: "Outbound" }].map(({ value, label }) => (
                                <button key={value} onClick={() => handleFilterChange("direction", value)}
                                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${filters.direction === value ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                                >{label}</button>
                              ))}
                            </div>
                          </div>

                          {/* Analysis Status */}
                          <div className="mb-4">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Analysis Status</p>
                            <div className="flex items-center bg-muted rounded-lg p-0.5 gap-0.5">
                              {[{ value: "", label: "All" }, { value: "computed", label: "Computed" }, { value: "pending", label: "Pending" }, { value: "failed", label: "Failed" }].map(({ value, label }) => (
                                <button key={value} onClick={() => handleFilterChange("analysisStatus", value)}
                                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${filters.analysisStatus === value ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                                >{label}</button>
                              ))}
                            </div>
                          </div>

                          {/* Call End Reason */}
                          <div className="mb-4">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Call ID</p>
                            <input type="text" value={filters.search} onChange={(e) => handleFilterChange("search", e.target.value)}
                              placeholder="Search by call ID or identifier..." className="w-full text-sm border border-input rounded-lg px-2.5 py-1.5 bg-background" />
                          </div>

                          <div className="mb-4">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Call End Reason</p>
                            <select value={filters.endReason} onChange={(e) => handleFilterChange("endReason", e.target.value)}
                              className="w-full text-sm border border-input rounded-lg px-2.5 py-1.5 bg-background">
                              <option value="">Any reason</option>
                              <option value="completed">Completed</option>
                              <option value="customer-ended">Customer ended</option>
                              <option value="assistant-ended">Assistant ended</option>
                              <option value="silence-timed-out">Silence timed out</option>
                              <option value="voicemail">Voicemail</option>
                            </select>
                          </div>

                          {/* Tag */}
                          <div className="mb-4">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Tag</p>
                            <input type="text" value={filters.tag} onChange={(e) => handleFilterChange("tag", e.target.value)}
                              placeholder="Filter by tag..." className="w-full text-sm border border-input rounded-lg px-2.5 py-1.5 bg-background" />
                          </div>

                          {/* Call Property Condition */}
                          <div className="mb-4">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Call Property</p>
                            {filters.callPropertyCondition ? (
                              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-amber-800">{filters.callPropertyCondition.property?.replace("metadata.", "metadata: ")}</span>
                                  <button onClick={() => handleFilterChange("callPropertyCondition", null)} className="text-amber-400 hover:text-amber-600">&times;</button>
                                </div>
                                <span className="text-amber-600">{filters.callPropertyCondition.operator}{filters.callPropertyCondition.value ? ` ${filters.callPropertyCondition.value}` : ""}</span>
                              </div>
                            ) : (
                              <CallPropertyBuilder
                                compact
                                onApply={(cond) => handleFilterChange("callPropertyCondition", cond)}
                              />
                            )}
                          </div>

                          {/* Metric Condition */}
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Metric Condition</p>
                            {filters.metricCondition ? (
                              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2 text-xs">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-indigo-800">
                                    {allMetrics.find((m) => m.id === filters.metricCondition.metric_id)?.name || "Metric"}
                                  </span>
                                  <button onClick={() => handleFilterChange("metricCondition", null)} className="text-indigo-400 hover:text-indigo-600">&times;</button>
                                </div>
                                <span className="text-indigo-600">
                                  {filters.metricCondition.field && `${filters.metricCondition.field} `}
                                  {filters.metricCondition.operator}{filters.metricCondition.value ? ` ${filters.metricCondition.value}` : ""}
                                </span>
                              </div>
                            ) : (
                              <div className="space-y-1.5">
                                <select value={mcMetricId} onChange={(e) => {
                                  setMcMetricId(e.target.value);
                                  const m = allMetrics.find((x) => x.id === parseInt(e.target.value));
                                  const fields = getFieldOptions(m);
                                  setMcField(fields?.[0]?.value || "");
                                  setMcOperator(getOperators(m)?.[0]?.value || "equals");
                                  setMcValue("");
                                }} className="w-full text-sm border border-input rounded-lg px-2.5 py-1.5 bg-background">
                                  <option value="">Select metric...</option>
                                  <optgroup label="Audio Metrics">
                                    {allMetrics.filter((m) => !m.is_custom && m.analysis_type === "audio").map((m) => (
                                      <option key={m.id} value={m.id}>{m.name}</option>
                                    ))}
                                  </optgroup>
                                  <optgroup label="LLM Metrics">
                                    {allMetrics.filter((m) => !m.is_custom && m.analysis_type !== "audio").map((m) => (
                                      <option key={m.id} value={m.id}>{m.name}</option>
                                    ))}
                                  </optgroup>
                                  <optgroup label="Custom Metrics">
                                    {allMetrics.filter((m) => m.is_custom).map((m) => (
                                      <option key={m.id} value={m.id}>{m.name}</option>
                                    ))}
                                  </optgroup>
                                </select>

                                {fieldOptions && (
                                  <select value={mcField} onChange={(e) => setMcField(e.target.value)}
                                    className="w-full text-sm border border-input rounded-lg px-2 py-1.5 bg-background">
                                    {fieldOptions.map((f) => (
                                      <option key={f.value} value={f.value}>{f.label}</option>
                                    ))}
                                  </select>
                                )}

                                <div className="flex gap-1.5">
                                  <select value={mcOperator} onChange={(e) => setMcOperator(e.target.value)}
                                    className="flex-1 text-sm border border-input rounded-lg px-2 py-1.5 bg-background">
                                    {operators.map((op) => (
                                      <option key={op.value} value={op.value}>{op.label}</option>
                                    ))}
                                  </select>
                                  {!hideValue && (
                                    resultType === "boolean" ? (
                                      <select value={mcValue} onChange={(e) => setMcValue(e.target.value)}
                                        className="flex-1 text-sm border border-input rounded-lg px-2 py-1.5 bg-background">
                                        <option value="">Select...</option>
                                        {BOOLEAN_VALUES.map((v) => (
                                          <option key={v.value} value={v.value}>{v.label}</option>
                                        ))}
                                      </select>
                                    ) : resultType === "rating" ? (
                                      <select value={mcValue} onChange={(e) => setMcValue(e.target.value)}
                                        className="flex-1 text-sm border border-input rounded-lg px-2 py-1.5 bg-background">
                                        <option value="">Select...</option>
                                        {[1,2,3,4,5].map((v) => <option key={v} value={v}>{v}/5</option>)}
                                      </select>
                                    ) : (
                                      <input type={resultType === "standard" || resultType === "numeric" ? "number" : "text"}
                                        value={mcValue} onChange={(e) => setMcValue(e.target.value)}
                                        placeholder="Value" className="flex-1 text-sm border border-input rounded-lg px-2 py-1.5 bg-background" />
                                    )
                                  )}
                                </div>

                                {mcMetricId && (
                                  <button onClick={() => {
                                    handleFilterChange("metricCondition", {
                                      metric_id: parseInt(mcMetricId),
                                      metric_key: selectedMetric?.standard_metric_key || null,
                                      field: fieldOptions ? mcField : null,
                                      operator: mcOperator,
                                      value: hideValue ? undefined : mcValue || undefined,
                                    });
                                    setMcMetricId(""); setMcField(""); setMcOperator("equals"); setMcValue("");
                                  }} className="w-full text-xs bg-indigo-600 text-white py-1.5 rounded-lg hover:bg-indigo-700 font-medium">
                                    Apply Metric Filter
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          {(filters.direction || filters.endReason || filters.analysisStatus || filters.tag || filters.dateFrom || filters.dateTo || filters.metricCondition || filters.callPropertyCondition) && (
                            <button onClick={() => {
                              setFilters({ direction: "", endReason: "", analysisStatus: "", tag: "", dateFrom: "", dateTo: "", metricCondition: null, callPropertyCondition: null });
                              setCurrentPage(1);
                            }} className="w-full text-center text-xs text-muted-foreground hover:text-foreground py-1.5 rounded-lg hover:bg-muted/50 transition-colors border-t border-border pt-3">
                              Clear all filters
                            </button>
                          )}
                        </div>
                        );
                      })()}
                    </>
                  );
                })()}
              </div>

              {/* Active filter chips */}
              {filters.direction && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                  {filters.direction}
                  <button
                    onClick={() => handleFilterChange("direction", "")}
                    className="hover:text-indigo-900 ml-0.5"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.endReason && (
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${END_REASON_STYLES[filters.endReason] || "bg-muted text-muted-foreground"} border-transparent`}
                >
                  {filters.endReason.replace(/-/g, " ")}
                  <button onClick={() => handleFilterChange("endReason", "")} className="ml-0.5 opacity-70 hover:opacity-100">×</button>
                </span>
              )}
              {filters.analysisStatus && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">
                  Status: {filters.analysisStatus}
                  <button onClick={() => handleFilterChange("analysisStatus", "")} className="ml-0.5 opacity-70 hover:opacity-100">×</button>
                </span>
              )}
              {filters.tag && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200">
                  Tag: {filters.tag}
                  <button onClick={() => handleFilterChange("tag", "")} className="ml-0.5 opacity-70 hover:opacity-100">×</button>
                </span>
              )}
              {(filters.dateFrom || filters.dateTo) && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  {filters.dateFrom && filters.dateTo ? `${filters.dateFrom} — ${filters.dateTo}` : filters.dateFrom ? `From ${filters.dateFrom}` : `Until ${filters.dateTo}`}
                  <button onClick={() => { handleFilterChange("dateFrom", ""); handleFilterChange("dateTo", ""); }} className="ml-0.5 opacity-70 hover:opacity-100">×</button>
                </span>
              )}
              {filters.callPropertyCondition && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                  {filters.callPropertyCondition.property} {filters.callPropertyCondition.operator}{filters.callPropertyCondition.value ? ` ${filters.callPropertyCondition.value}` : ""}
                  <button onClick={() => handleFilterChange("callPropertyCondition", null)} className="ml-0.5 opacity-70 hover:opacity-100">×</button>
                </span>
              )}
              {filters.metricCondition && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                  {allMetrics.find((m) => m.id === filters.metricCondition.metric_id)?.name || "Metric"}{" "}
                  {filters.metricCondition.field && `${filters.metricCondition.field} `}
                  {filters.metricCondition.operator} {filters.metricCondition.value}
                  <button onClick={() => handleFilterChange("metricCondition", null)} className="ml-0.5 opacity-70 hover:opacity-100">×</button>
                </span>
              )}
              {Object.entries(columnFilters).map(([colKey, val]) => {
                const col = allColumnDefs.find((c) => c.key === colKey);
                return (
                  <span key={colKey} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 capitalize">
                    {col?.label}: {val}
                    <button onClick={() => setColumnFilters((p) => { const n = { ...p }; delete n[colKey]; return n; })} className="ml-0.5 opacity-70 hover:opacity-100">×</button>
                  </span>
                );
              })}
            </div>

            {/* Right: selection actions + columns + add */}
            <div className="flex items-center gap-2">
              {canReanalyze && selectedIds.length > 0 && (
                <>
                  <span className="text-sm text-muted-foreground font-medium">
                    {selectedIds.length} selected
                  </span>
                  <div className="relative">
                    <button
                      onClick={() => setRerunDropdownOpen((v) => !v)}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-border rounded-lg bg-card hover:bg-muted/50 transition-colors"
                    >
                      <RefreshCw className="h-4 w-4 text-muted-foreground" />
                      <span>Rerun</span>
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </button>
                    {rerunDropdownOpen && rerunMode === "menu" && (
                      <div className="absolute right-0 mt-1 w-52 bg-popover border border-border rounded-lg shadow-lg z-20">
                        <button
                          onClick={() => handleRerun("all")}
                          className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted/50 rounded-t-lg"
                        >
                          All Metrics
                        </button>
                        <button
                          onClick={() => setRerunMode("select")}
                          disabled={allMetrics.length === 0}
                          className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted/50 rounded-b-lg border-t border-border disabled:opacity-50"
                        >
                          Specific Metrics…
                        </button>
                      </div>
                    )}
                    {rerunDropdownOpen && rerunMode === "select" && (
                      <div className="absolute right-0 mt-1 w-64 bg-popover border border-border rounded-lg shadow-lg z-20">
                        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                          <span className="text-xs font-medium text-muted-foreground">
                            Metrics to rerun
                          </span>
                          <button
                            onClick={() =>
                              setRerunMetricIds(
                                rerunMetricIds.length === allMetrics.length
                                  ? []
                                  : allMetrics.map((m) => m.id)
                              )
                            }
                            className="text-xs text-primary hover:underline"
                          >
                            {rerunMetricIds.length === allMetrics.length ? "Clear" : "All"}
                          </button>
                        </div>
                        <div className="max-h-60 overflow-y-auto py-1">
                          {allMetrics.map((m) => (
                            <label
                              key={m.id}
                              className="flex items-center gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-muted/50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={rerunMetricIds.includes(m.id)}
                                onChange={() => toggleRerunMetric(m.id)}
                                className="h-3.5 w-3.5 rounded border-border"
                              />
                              <span className="truncate">{m.name}</span>
                            </label>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2 border-t border-border">
                          <button
                            onClick={() => setRerunMode("menu")}
                            className="text-xs text-muted-foreground hover:text-foreground"
                          >
                            Back
                          </button>
                          <button
                            onClick={handleRerunSelectedMetrics}
                            disabled={rerunMetricIds.length === 0}
                            className="ml-auto px-3 py-1.5 text-xs font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50"
                          >
                            Rerun {rerunMetricIds.length || ""}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleDeleteSelected}
                    disabled={deleting}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-red-200 text-red-600 rounded-lg bg-card hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>{deleting ? "Deleting…" : "Delete"}</span>
                  </button>
                </>
              )}
              {canFetchCalls && (
                <button
                  onClick={handleFetchCalls}
                  disabled={fetchingCalls}
                  title="Pull recent calls from the provider for this agent"
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-border rounded-lg bg-card hover:bg-muted/50 transition-colors disabled:opacity-50"
                >
                  <RefreshCw
                    className={`h-4 w-4 text-muted-foreground ${fetchingCalls ? "animate-spin" : ""}`}
                  />
                  <span>{fetchingCalls ? "Fetching…" : "Fetch Calls"}</span>
                </button>
              )}
              {contextAgents.length > 0 && (
                <AgentPicker agents={contextAgents} selectedAgent={selectedAgent} setSelectedAgent={setSelectedAgent} onAdd={() => navigate("/ai-testing-agent/create")} />
              )}
              <div className="relative" ref={columnsMenuRef}>
                <button
                  onClick={() => setShowColumnsMenu((v) => !v)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-border rounded-lg bg-card hover:bg-muted/50 transition-colors"
                >
                  <Columns2 className="h-4 w-4 text-muted-foreground" />
                  Columns
                </button>
                {showColumnsMenu && (
                  <div className="absolute right-0 mt-1 w-56 bg-popover border border-border rounded-lg shadow-lg z-20 p-1.5 max-h-80 overflow-auto">
                    {BASE_COLUMNS.map((col) => (
                      <label
                        key={col.key}
                        className="flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns.has(col.key)}
                          onChange={() => toggleColumn(col.key)}
                          className="h-4 w-4 rounded border-input text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-foreground">{col.label}</span>
                      </label>
                    ))}
                    {metricColumns.length > 0 && (
                      <>
                        <div className="h-px bg-border my-1.5" />
                        <p className="px-2.5 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Metrics</p>
                        {metricColumns.map((col) => (
                          <label
                            key={col.key}
                            className="flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={visibleColumns.has(col.key)}
                              onChange={() => toggleColumn(col.key)}
                              className="h-4 w-4 rounded border-input text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-foreground truncate">{col.label}</span>
                            {col.isCritical && <span className="text-[9px] font-medium text-red-500">Critical</span>}
                          </label>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
              {canManualUpload && (
                <Button
                  variant="primary"
                  onClick={() => setIsUploadModalOpen(true)}
                >
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  <span>Add Call</span>
                </Button>
              )}
            </div>
          </div>

          {/* Table area: fills the remaining height. Its CSS-computed height drives
              the page size so the table fits without scrolling. */}
          <div ref={tableAreaRef} className="flex-1 min-h-0 overflow-hidden">
          {/* Table or empty state */}
          {calls.length === 0 ? (
            <div className="text-center py-16 border border-border rounded-lg bg-card">
              <div className="mx-auto w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3">
                <Search className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">No calls found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedAgent ? `No calls found for ${selectedAgent.name}.` : "Try adjusting your filters."}
              </p>
            </div>
          ) : (
          <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin">
              <table className="min-w-full divide-y divide-border" style={{ tableLayout: "fixed", width: tableWidth }}>
                <colgroup>
                  <col style={{ width: CHECKBOX_COL_WIDTH }} />
                  {orderedVisibleColumns.map((col) => (
                    <col key={col.key} style={{ width: colWidthFor(col.key) }} />
                  ))}
                </colgroup>
                <thead className="bg-muted sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 bg-muted">
                      <input
                        type="checkbox"
                        checked={
                          selectedIds.length === displayCalls.length &&
                          displayCalls.length > 0
                        }
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-input text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      />
                    </th>
                    {orderedVisibleColumns.map((col) => {
                      const isSorted = sortConfig.key === col.key;
                      const fcRaw = getFilterConfig(col.key);
                      // Never render a select filter that would only show "All" with no real options.
                      const fc = fcRaw && fcRaw.type === "select" && !(fcRaw.options?.length) ? null : fcRaw;
                      const hasFilter = !!columnFilters[col.key];
                      return (
                        <th
                          key={col.key}
                          onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.key); }}
                          onDrop={() => handleColumnDrop(col.key)}
                          className={`relative px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground whitespace-nowrap select-none transition-colors ${
                            dragOverCol === col.key && draggedCol !== col.key ? "bg-indigo-50 border-l-2 border-indigo-400" : ""
                          } ${draggedCol === col.key ? "opacity-40 bg-muted" : ""}`}
                        >
                          <div
                            className="flex items-center gap-1 min-w-0 pr-1"
                            draggable
                            onDragStart={() => setDraggedCol(col.key)}
                            onDragEnd={() => { setDraggedCol(null); setDragOverCol(null); }}
                          >
                            <GripVertical className="h-3 w-3 text-muted-foreground/50 flex-shrink-0 cursor-grab active:cursor-grabbing" />
                            <button
                              onClick={() => handleSort(col.key)}
                              className={`flex items-center gap-1 min-w-0 hover:text-foreground transition-colors ${isSorted ? "text-indigo-600" : ""}`}
                            >
                              <span className="truncate">{col.label}</span>
                              {isSorted ? (
                                sortConfig.direction === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                              ) : (
                                <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />
                              )}
                            </button>
                            {fc && (
                              <div className="relative">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setActiveFilterCol(activeFilterCol === col.key ? null : col.key); }}
                                  className={`p-0.5 rounded hover:bg-muted transition-colors ${hasFilter ? "text-indigo-600" : "text-muted-foreground/50 hover:text-muted-foreground"}`}
                                >
                                  <Filter className="h-3 w-3" />
                                </button>
                                {activeFilterCol === col.key && (
                                  <div ref={filterDropdownRef} className="absolute top-full left-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-30 py-1" style={{ minWidth: fc.type === "search" ? 180 : 136 }}>
                                    {fc.type === "select" ? (
                                      <>
                                        <button
                                          onClick={() => { setColumnFilters((p) => { const n = { ...p }; delete n[col.key]; return n; }); setActiveFilterCol(null); }}
                                          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 ${!hasFilter ? "text-indigo-600 font-medium" : "text-muted-foreground"}`}
                                        >
                                          All
                                        </button>
                                        {fc.options.map((opt) => (
                                          <button
                                            key={opt.value}
                                            onClick={() => { setColumnFilters((p) => ({ ...p, [col.key]: opt.value })); setActiveFilterCol(null); }}
                                            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 capitalize ${columnFilters[col.key] === opt.value ? "text-indigo-600 font-medium" : "text-foreground"}`}
                                          >
                                            {opt.label}
                                          </button>
                                        ))}
                                      </>
                                    ) : (
                                      <div className="px-2 py-1.5">
                                        <input
                                          type="text"
                                          autoFocus
                                          placeholder="Search..."
                                          value={columnFilters[col.key] || ""}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setColumnFilters((p) => {
                                              if (!v) { const n = { ...p }; delete n[col.key]; return n; }
                                              return { ...p, [col.key]: v };
                                            });
                                          }}
                                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setActiveFilterCol(null); }}
                                          className="w-full px-2 py-1.5 text-xs border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
                                        />
                                        {hasFilter && (
                                          <button
                                            onClick={() => { setColumnFilters((p) => { const n = { ...p }; delete n[col.key]; return n; }); setActiveFilterCol(null); }}
                                            className="w-full text-center text-[10px] text-muted-foreground hover:text-foreground mt-1"
                                          >
                                            Clear
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {/* Drag the right edge to resize this column */}
                          <div
                            onMouseDown={(e) => startResize(e, col.key)}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none hover:bg-indigo-400/50 active:bg-indigo-500"
                            title="Drag to resize"
                          />
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {displayCalls.map((call) => (
                    <tr
                      key={call.id}
                      className={`hover:bg-muted/50 transition-colors ${selectedIds.includes(call.id) ? "bg-indigo-50" : ""}`}
                    >
                      <td
                        className="w-10 px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(call.id)}
                          onChange={() => toggleSelectOne(call.id)}
                          className="h-4 w-4 rounded border-input text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                        />
                      </td>
                      {orderedVisibleColumns.map((col) =>
                        renderCell(call, col),
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}
          </div>

          {/* Pagination — always reserves its height so the table area size (and
              therefore the computed page size) stays stable whether or not there
              is more than one page. */}
          <div className="shrink-0 min-h-[34px]">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      )}

      <CallUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        clientId={currentWorkspace?.id}
        agents={agents}
        defaultAgentId={selectedAgent?.id || null}
        onSuccess={handleUploadSuccess}
      />
    </div>
  );
};

export default Conversations;
